import type { Story } from '../data'
import { loadRuntimeConfig, type AuthMode, type RuntimeConfig } from './runtime'
import { AUTH_STORAGE_KEY, readStoredAuth, saveStoredAuth, type StoredAuth } from './auth-storage'

export type ServerCapabilities = { voiceUpload: boolean; voiceClone: boolean; storyGeneration: boolean; provider: string }
export type ServerHealth = { status: string; version: string; capabilities: ServerCapabilities }
export type ServerConnection =
  | { state: 'checking'; configured: boolean; baseUrl?: string }
  | { state: 'local-only'; configured: false }
  | { state: 'connected'; configured: true; baseUrl: string; authMode: AuthMode; health: ServerHealth }
  | { state: 'unreachable'; configured: true; baseUrl: string; authMode: AuthMode; message: string }
export type ServerUser = { id: string; email: string; name: string }
export type ServerAuthSession = { authenticated: boolean; user: ServerUser | null }
export type AuthCredentials = { email: string; password: string }
export type Registration = AuthCredentials & { name: string }
export type ConsentScope = 'voice_upload' | 'voice_clone' | 'story_generation' | 'voice_conversion_reference'
export type ServerConsent = { id: string; status: string; scopes: ConsentScope[] }
export type ServerRecording = { id: string; status: string; filename: string; contentType: string; sizeBytes: number; createdAt: string }
export type ServerVoiceProfile = { id: string; name: string; locale: string; status: 'queued' | 'preparing' | 'ready' | 'failed' | 'deleted'; provider: string; error?: string; errorCode?: string; createdAt: string; approvalStatus?: 'pending' | 'approved' | 'rejected'; lifecycle?: 'candidate' | 'active' | 'retired'; previewStatus?: string; previewChecksum?: string; previewUrl?: string; conversionReferenceStatus?: 'original_embedding' | 'original_recording' | 'synthetic_preview' | 'unavailable' | 'rerecord_required'; conversionModelRevision?: string; conversionTargetModelRevision?: string }
export type ServerConversionReference = { voiceProfileId: string; status: string; qualityStatus?: string | null; sizeBytes?: number | null; checksumSha256?: string | null; durationSeconds?: number | null; voicedSecondsEstimate?: number | null; consentId?: string | null; createdAt?: string | null; updatedAt?: string | null; expiresAt?: string | null; conversionReady: boolean; modelRevision?: string | null }
export type GenerationRequest = { storyId: Story['id']; voiceProfileId: string; quoteId?: string; style?: string; speed?: number; experimentalPreview?: boolean }
export type GenerationQuote = { quoteId: string; expiresAt: string; storyVersionId: string; units: number; reservationRequired: boolean; action: 'create' | 'reuse' | 'restore'; existingJobId?: string; blockingCode?: string; experimentalPreview?: boolean }
export type Usage = { windowStart?: string; windowEnd?: string; limitUnits: number; reservedUnits: number; consumedUnits: number; availableUnits: number; trialAvailable: boolean; admissionsRemaining: number }
export type LibraryItem = { story?: Story; storyId: string; title: string; job?: GenerationJob; voiceName?: string; contentVersionId?: string; assetState?: string; entitlementType?: string }
export type BillingPlan = { code: string; productCode?: string; title?: string; name?: string; amount?: number; currency?: string; priceVersion?: number; generationUnits?: number; saleable?: boolean; productType?: string }
export type BillingPlans = { enabled: boolean; items?: BillingPlan[]; plans?: BillingPlan[] }
export type SubscriptionInfo = { id: string; state: string; currentPeriodStart: string; currentPeriodEnd: string; cancelAtPeriodEnd: boolean; product?: Record<string, unknown> }
export type BillingOrder = { id: string; state: string; createdAt: string; product?: Record<string, unknown> }
export type CheckoutSession = { orderId: string; redirectUrl?: string | null; expiresAt: string; provider?: string; testOnly?: boolean; checkout?: { kind: 'payment' | 'billingAuth'; clientKey: string; customerKey: string; method: 'CARD'; amount: { currency: 'KRW'; value: number }; orderId: string; orderName: string; successUrl: string; failUrl: string } }
export type GenerationJob = {
  id: string
  provider?: string
  modelRevision?: string
  pipelineVersion?: string
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled' | 'deleted'
  progress: number
  voiceProfileId: string
  storyId?: string
  outputUrl?: string
  error?: string
  createdAt: string
  updatedAt: string
  phase?: string
  completedSegments?: number
  totalSegments?: number
  assetState?: string
  expiresAt?: string
  downloadAllowed?: boolean
  units?: number
  voiceName?: string
  contentVersionId?: string
  checksumSha256?: string
  sizeBytes?: number
  outputSize?: number
  durationSeconds?: number
  errorCode?: string
  canRetry?: boolean
  experimentalPreview?: boolean
  qualityStatus?: string
  qualityWarningCount?: number
}
export type PrivacyPurgeReceipt = { receiptId: string; scope: 'account'; resourceCount: number; completedAt: string }

export interface VoiceGenerationService {
  setAccessToken(token: string | null): void
  authSession(): ServerAuthSession
  onAuthChange(listener: (session: ServerAuthSession) => void): () => void
  login(input: AuthCredentials): Promise<ServerAuthSession>
  register(input: Registration): Promise<ServerAuthSession>
  logout(): void
  restoreSession(): Promise<ServerAuthSession>
  connection(force?: boolean): Promise<ServerConnection>
  runtime(): Promise<RuntimeConfig>
  createConsent(input: { subjectName: string; relationship: string; scopes: ConsentScope[]; policyVersion: string }): Promise<ServerConsent>
  uploadRecording(input: { file: Blob; consentId: string; segmentIndex: number; promptText?: string }): Promise<ServerRecording>
  deleteRecording(id: string): Promise<void>
  revokeConsent(id: string): Promise<ServerConsent>
  createVoiceProfile(input: { name: string; locale: string; consentId: string; recordingIds: string[] }): Promise<ServerVoiceProfile>
  listVoiceProfiles(): Promise<ServerVoiceProfile[]>
  voiceProfileStatus(id: string): Promise<ServerVoiceProfile>
  deleteVoiceProfile(id: string): Promise<void>
  conversionReference(id: string): Promise<ServerConversionReference>
  uploadConversionReference(id: string, file: Blob, consentId: string): Promise<ServerConversionReference>
  deleteConversionReference(id: string): Promise<void>
  createGeneration(request: GenerationRequest, idempotencyKey: string): Promise<GenerationJob>
  generationStatus(jobId: string): Promise<GenerationJob>
  listGenerations(): Promise<GenerationJob[]>
  generationAudio(jobId: string): Promise<Blob>
  generationDownload(jobId: string): Promise<Blob>
  deleteGeneration(jobId: string): Promise<void>
  purgeAccount(): Promise<PrivacyPurgeReceipt>
  catalog(): Promise<Story[]>
  library(tab?: string): Promise<LibraryItem[]>
  usage(): Promise<Usage>
  preview(id: string): Promise<Blob>
  approveProfile(id: string, checksum: string): Promise<ServerVoiceProfile>
  retryProfile(id: string): Promise<ServerVoiceProfile>
  quote(request: GenerationRequest): Promise<GenerationQuote>
  generationAction(id: string, action: 'cancel' | 'retry' | 'restore'): Promise<GenerationJob>
  billingPlans(): Promise<BillingPlans>
  checkout(input: { productCode: string; priceVersion: number; storyId?: string }): Promise<CheckoutSession>
  confirmPayment(input: { paymentKey: string; orderId: string; amount: number }): Promise<{ id: string; state: string; entitlementState?: string; verificationPending?: boolean }>
  activateSubscription(input: { orderId: string; authKey: string; customerKey: string }): Promise<{ id: string; state: string; entitlementState?: string; verificationPending?: boolean }>
  order(id: string): Promise<{ state: string; entitlementState?: string }>
  cancelSubscription(): Promise<unknown>
  subscription(): Promise<SubscriptionInfo | null>
  orders(): Promise<BillingOrder[]>
  passwordReset(email: string): Promise<unknown>
  passwordConfirm(token: string, password: string): Promise<unknown>
  verificationRequest(): Promise<unknown>
  verificationConfirm(token: string): Promise<unknown>
  favorites(): Promise<string[]>
  setFavorite(id: string, saved: boolean): Promise<unknown>
}

export class ServerNotConfiguredError extends Error {
  constructor() { super('음성 생성 서버 주소가 설정되지 않았습니다.'); this.name = 'ServerNotConfiguredError' }
}
export class ServerUnavailableError extends Error {
  constructor(message = '음성 서버에 연결할 수 없습니다.') { super(message); this.name = 'ServerUnavailableError' }
}
export class AuthenticationRequiredError extends Error {
  constructor() { super('서버 인증 설정이 필요합니다.'); this.name = 'AuthenticationRequiredError' }
}
export class ApiError extends Error {
  constructor(message: string, public status: number, public code?: string) { super(message); this.name = 'ApiError' }
}

export class HttpVoiceGenerationService implements VoiceGenerationService {
  private credentials: StoredAuth | null = readStoredAuth()
  private get token() { return this.credentials?.accessToken ?? null }
  private get user() { return this.credentials?.user ?? null }
  private authListeners = new Set<(session: ServerAuthSession) => void>()
  private epoch = 0
  private controllers = new Set<AbortController>()
  private refreshing?: Promise<string>
  private persisted = Boolean(this.credentials)
  private authAttempt = 0
  private developmentLogin?: Promise<string>

  constructor() {
    window.addEventListener('storage', event => {
      if (event.key !== AUTH_STORAGE_KEY && event.key !== null) return
      const updated = readStoredAuth()
      if (updated?.accessToken === this.token && updated?.user?.id === this.user?.id) return
      const sameOwner = updated?.user?.id === this.user?.id && Boolean(updated) && Boolean(this.credentials)
      this.credentials = updated
      this.persisted = Boolean(updated)
      if (!sameOwner) { this.epoch += 1; this.controllers.forEach(controller => controller.abort()); this.emitAuthChange() }
    })
  }

  setAccessToken(token: string | null) {
    this.epoch += 1
    this.authAttempt += 1
    this.controllers.forEach(controller => controller.abort())
    this.credentials = token ? { accessToken: token, user: this.user } : null
    this.persisted = saveStoredAuth(this.credentials) && Boolean(this.credentials)
    this.emitAuthChange()
  }

  authSession(): ServerAuthSession { return { authenticated: Boolean(this.token), user: this.user } }
  onAuthChange(listener: (session: ServerAuthSession) => void) {
    this.authListeners.add(listener)
    return () => this.authListeners.delete(listener)
  }
  async login(input: AuthCredentials) {
    const { expectedEpoch, attempt } = this.beginAuthIntent()
    const response = await this.request<AuthResponse>('/auth/token', { method: 'POST', authenticated: false, body: JSON.stringify(input) })
    return this.acceptAuth(response, expectedEpoch, attempt)
  }
  async register(input: Registration) {
    const { expectedEpoch, attempt } = this.beginAuthIntent()
    const response = await this.request<AuthResponse>('/auth/register', { method: 'POST', authenticated: false, body: JSON.stringify(input) })
    return this.acceptAuth(response, expectedEpoch, attempt)
  }
  logout() {
    const refreshToken = this.credentials?.refreshToken
    const accessToken = this.token
    this.setAccessToken(null)
    this.revokeSession(refreshToken, accessToken)
  }
  async restoreSession(): Promise<ServerAuthSession> {
    if (!this.token) return this.authSession()
    const expectedEpoch = this.epoch
    const user = await this.request<ServerUser>('/auth/me')
    if (expectedEpoch !== this.epoch || !this.credentials) throw new AuthenticationRequiredError()
    if (this.user && user.id !== this.user.id) { this.setAccessToken(null); throw new AuthenticationRequiredError() }
    const changed = !this.user || this.user.email !== user.email || this.user.name !== user.name
    this.credentials = { ...this.credentials, user }
    this.persisted = saveStoredAuth(this.credentials)
    if (changed) this.emitAuthChange()
    return this.authSession()
  }

  async runtime() { return loadRuntimeConfig() }
  async connection(force = false): Promise<ServerConnection> {
    const config = await loadRuntimeConfig(force)
    if (!config.apiBaseUrl) return { state: 'local-only', configured: false }
    try {
      const health = await this.request<ServerHealth>('/health', { authenticated: false })
      return { state: 'connected', configured: true, baseUrl: config.apiBaseUrl, authMode: config.authMode, health }
    } catch (error) {
      return { state: 'unreachable', configured: true, baseUrl: config.apiBaseUrl, authMode: config.authMode, message: readableServerError(error) }
    }
  }
  async createConsent(input: { subjectName: string; relationship: string; scopes: ConsentScope[]; policyVersion: string }) {
    return this.request<ServerConsent>('/consents', { method: 'POST', body: JSON.stringify({ ...input, confirmed: true }) })
  }
  async uploadRecording({ file, consentId, segmentIndex, promptText }: { file: Blob; consentId: string; segmentIndex: number; promptText?: string }) {
    const data = new FormData()
    const { recordingForUpload } = await import('./recorder')
    const upload = await recordingForUpload(file)
    data.append('file', upload, `segment-${segmentIndex + 1}.${extensionFor(upload.type)}`)
    data.append('consent_id', consentId)
    data.append('segment_index', String(segmentIndex))
    if (promptText) data.append('prompt_text', promptText)
    return this.request<ServerRecording>('/recordings', { method: 'POST', body: data })
  }
  async deleteRecording(id: string) { await this.request<void>(`/recordings/${encodeURIComponent(id)}`, { method: 'DELETE' }) }
  async revokeConsent(id: string) { return this.request<ServerConsent>(`/consents/${encodeURIComponent(id)}`, { method: 'DELETE' }) }
  async createVoiceProfile(input: { name: string; locale: string; consentId: string; recordingIds: string[] }) {
    return this.request<ServerVoiceProfile>('/voice-profiles', { method: 'POST', body: JSON.stringify(input) })
  }
  async listVoiceProfiles() { return this.request<ServerVoiceProfile[]>('/voice-profiles') }
  async voiceProfileStatus(id: string) { return this.request<ServerVoiceProfile>(`/voice-profiles/${encodeURIComponent(id)}`) }
  async deleteVoiceProfile(id: string) { await this.request<void>(`/voice-profiles/${encodeURIComponent(id)}`, { method: 'DELETE' }) }
  async conversionReference(id: string) { return this.request<ServerConversionReference>(`/voice-profiles/${encodeURIComponent(id)}/conversion-reference`) }
  async uploadConversionReference(id: string, file: Blob, consentId: string) {
    if (!file.size || file.size > 64 * 1024 * 1024 || file.type !== 'audio/wav') throw new Error('확인한 WAV 원본만 64MB 이하로 전송할 수 있어요.')
    const data = new FormData()
    data.append('file', file, 'parent-reference.wav')
    data.append('consent_id', consentId)
    return this.request<ServerConversionReference>(`/voice-profiles/${encodeURIComponent(id)}/conversion-reference`, { method: 'POST', body: data, timeoutMs: 300_000 })
  }
  async deleteConversionReference(id: string) { await this.request<void>(`/voice-profiles/${encodeURIComponent(id)}/conversion-reference`, { method: 'DELETE' }) }
  async createGeneration(request: GenerationRequest, idempotencyKey: string) {
    return this.request<GenerationJob>('/generations', { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(request) })
  }
  async generationStatus(jobId: string) { return this.request<GenerationJob>(`/generations/${encodeURIComponent(jobId)}`) }
  async listGenerations() { return this.request<GenerationJob[]>('/generations') }
  async generationAudio(jobId: string) { return this.request<Blob>(`/generations/${encodeURIComponent(jobId)}/audio`, { responseType: 'blob' }) }
  async generationDownload(jobId: string) { return this.request<Blob>(`/generations/${encodeURIComponent(jobId)}/download`, { responseType: 'blob' }) }
  async deleteGeneration(jobId: string) { await this.request<void>(`/generations/${encodeURIComponent(jobId)}`, { method: 'DELETE' }) }
  async purgeAccount() {
    const receipt = await this.request<PrivacyPurgeReceipt>('/privacy/purge', { method: 'POST', body: JSON.stringify({ confirm: true }) })
    this.setAccessToken(null)
    return receipt
  }
  async catalog() {
    const items: Story[] = []; let cursor: string | undefined
    do { const result = await this.request<Story[] | { items: Story[]; nextCursor?: string }>(`/stories?limit=50${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`, { authenticated: false, cache: 'no-store' }); if (Array.isArray(result)) return result; items.push(...result.items); cursor = result.nextCursor } while (cursor)
    return items
  }
  async library(tab = 'working') { const items: LibraryItem[] = []; let cursor: string | undefined; do { const result = await this.request<{ items: LibraryItem[]; nextCursor?: string }>(`/me/library?tab=${encodeURIComponent(tab)}&limit=50${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`); items.push(...result.items); cursor = result.nextCursor } while (cursor); return items }
  async usage() { return this.request<Usage>('/me/usage') }
  async preview(id: string) { return this.request<Blob>(`/voice-profiles/${encodeURIComponent(id)}/preview`, { responseType: 'blob' }) }
  async approveProfile(id: string, checksum: string) { return this.request<ServerVoiceProfile>(`/voice-profiles/${encodeURIComponent(id)}/approve`, { method: 'POST', body: JSON.stringify({ previewChecksum: checksum, confirmed: true }) }) }
  async retryProfile(id: string) { return this.request<ServerVoiceProfile>(`/voice-profiles/${encodeURIComponent(id)}/retry`, { method: 'POST' }) }
  async quote(request: GenerationRequest) { return this.request<GenerationQuote>('/generations/quote', { method: 'POST', body: JSON.stringify(request) }) }
  async generationAction(id: string, action: 'cancel' | 'retry' | 'restore') { return this.request<GenerationJob>(`/generations/${encodeURIComponent(id)}/${action}`, { method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID() } }) }
  async billingPlans() { const response = await this.request<BillingPlans>('/billing/plans', { authenticated: false }); return { ...response, items: (response.items ?? response.plans ?? []).map(item => ({ ...item, code: item.productCode ?? item.code, name: item.title ?? item.name })) } }
  async checkout(input: { productCode: string; priceVersion: number; storyId?: string }) { return this.request<CheckoutSession>('/billing/checkouts', { method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID() }, body: JSON.stringify(input) }) }
  async confirmPayment(input: { paymentKey: string; orderId: string; amount: number }) { return this.request<{ id: string; state: string; entitlementState?: string; verificationPending?: boolean }>('/billing/confirm', { method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID() }, body: JSON.stringify(input) }) }
  async activateSubscription(input: { orderId: string; authKey: string; customerKey: string }) { return this.request<{ id: string; state: string; entitlementState?: string; verificationPending?: boolean }>('/billing/subscriptions/activate', { method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID() }, body: JSON.stringify(input) }) }
  async order(id: string) { return this.request<{ state: string; entitlementState?: string }>(`/billing/orders/${encodeURIComponent(id)}`) }
  async cancelSubscription() { return this.request('/billing/subscription/cancel', { method: 'POST' }) }
  async subscription() { const data = await this.request<{ subscription: SubscriptionInfo | null }>('/billing/subscription'); return data.subscription }
  async orders() { const data = await this.request<{ items: BillingOrder[] }>('/billing/orders'); return data.items }
  async passwordReset(email: string) { return this.request('/auth/password-reset/request', { method: 'POST', authenticated: false, body: JSON.stringify({ email }) }) }
  async passwordConfirm(token: string, password: string) { return this.request('/auth/password-reset/confirm', { method: 'POST', authenticated: false, body: JSON.stringify({ token, password }) }) }
  async verificationRequest() { return this.request('/auth/email-verification/request', { method: 'POST', authenticated: false, body: JSON.stringify({ email: this.user?.email }) }) }
  async verificationConfirm(token: string) { return this.request('/auth/email-verification/confirm', { method: 'POST', authenticated: false, body: JSON.stringify({ token }) }) }
  async favorites() { const data = await this.request<string[] | { items: (string | { storyId: string })[] }>('/me/favorites'); return (Array.isArray(data) ? data : data.items).map(item => typeof item === 'string' ? item : item.storyId) }
  async setFavorite(id: string, saved: boolean) { return this.request(`/me/favorites/${encodeURIComponent(id)}`, { method: saved ? 'PUT' : 'DELETE' }) }

  private async ensureToken(config: RuntimeConfig) {
    if (this.token) {
      if (this.credentials?.expiresAt && this.credentials.expiresAt <= Date.now() + 60_000 && this.credentials.refreshToken) return this.refreshAccessToken()
      return this.token
    }
    if (config.authMode === 'external') throw new AuthenticationRequiredError()
    if (this.developmentLogin) return this.developmentLogin
    const expectedEpoch = this.epoch; const attempt = ++this.authAttempt
    const operation = this.request<AuthResponse>('/auth/dev-token', {
      method: 'POST', authenticated: false,
      body: JSON.stringify({ email: config.devAuthEmail, name: config.devAuthName }),
    }).then(response => { this.acceptAuth(response, expectedEpoch, attempt); return response.accessToken })
    const tracked = operation.finally(() => { if (this.developmentLogin === tracked) this.developmentLogin = undefined })
    this.developmentLogin = tracked
    return tracked
  }

  private responseCredentials(response: AuthResponse): StoredAuth {
    if (!response.accessToken || !response.user?.id) throw new ApiError('로그인 정보를 확인하지 못했어요. 다시 시도해 주세요.', 502, 'INVALID_AUTH_RESPONSE')
    return { accessToken: response.accessToken, user: response.user,
      ...(response.expiresIn > 0 ? { expiresAt: Date.now() + response.expiresIn * 1000 } : {}),
      ...(response.refreshToken ? { refreshToken: response.refreshToken } : {}),
      ...(response.refreshExpiresIn && response.refreshExpiresIn > 0 ? { refreshExpiresAt: Date.now() + response.refreshExpiresIn * 1000 } : {}) }
  }

  private beginAuthIntent() {
    this.epoch += 1
    this.controllers.forEach(controller => controller.abort())
    return { expectedEpoch: this.epoch, attempt: ++this.authAttempt }
  }

  private revokeSession(refreshToken?: string, accessToken?: string | null) {
    if (!refreshToken) return
    void this.request<void>('/auth/logout', { method: 'POST', authenticated: false,
      ...(accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {}), body: JSON.stringify({ refreshToken }) }).catch(() => undefined)
  }

  private acceptAuth(response: AuthResponse, expectedEpoch: number, attempt: number): ServerAuthSession {
    if (expectedEpoch !== this.epoch || attempt !== this.authAttempt) {
      this.revokeSession(response.refreshToken, response.accessToken)
      throw new AuthenticationRequiredError()
    }
    const credentials = this.responseCredentials(response)
    this.epoch += 1
    if (this.credentials) this.controllers.forEach(controller => controller.abort())
    this.credentials = credentials
    this.persisted = saveStoredAuth(credentials)
    this.emitAuthChange()
    return this.authSession()
  }

  private refreshAccessToken(): Promise<string> {
    if (this.refreshing) return this.refreshing
    const expectedEpoch = this.epoch
    const original = this.credentials
    const run = async () => {
      if (expectedEpoch !== this.epoch || !original?.refreshToken) throw new AuthenticationRequiredError()
      // Web Locks serialize refresh rotation between windows of the same app origin.
      const stored = readStoredAuth()
      if (this.persisted && (!stored || stored.user?.id !== original.user?.id)) {
        this.credentials = stored; this.persisted = Boolean(stored); this.epoch += 1
        this.controllers.forEach(controller => controller.abort()); this.emitAuthChange()
        throw new AuthenticationRequiredError()
      }
      if (stored && stored.user?.id === original.user?.id && stored.accessToken !== original.accessToken) {
        this.credentials = stored
        return stored.accessToken
      }
      if (original.refreshExpiresAt && original.refreshExpiresAt <= Date.now()) { this.setAccessToken(null); throw new AuthenticationRequiredError() }
      try {
        const response = await this.request<AuthResponse>('/auth/refresh', { method: 'POST', authenticated: false, body: JSON.stringify({ refreshToken: original.refreshToken }) })
        if (expectedEpoch !== this.epoch || !this.credentials || this.user?.id !== response.user.id) {
          this.revokeSession(response.refreshToken, response.accessToken)
          throw new AuthenticationRequiredError()
        }
        const latest = readStoredAuth()
        if (this.persisted && (!latest || latest.user?.id !== original.user?.id)) {
          this.credentials = latest; this.persisted = Boolean(latest); this.epoch += 1
          this.controllers.forEach(controller => controller.abort()); this.emitAuthChange()
          this.revokeSession(response.refreshToken, response.accessToken)
          throw new AuthenticationRequiredError()
        }
        this.credentials = this.responseCredentials(response)
        this.persisted = saveStoredAuth(this.credentials)
        return response.accessToken
      } catch (error) {
        if (expectedEpoch === this.epoch && error instanceof ApiError && [401, 403].includes(error.status)) {
          // Older WebViews may lack Web Locks; a winning window can already have rotated this session.
          const latest = readStoredAuth()
          if (latest && latest.user?.id === original.user?.id && latest.accessToken !== original.accessToken) { this.credentials = latest; return latest.accessToken }
          if (latest && latest.user?.id !== original.user?.id) {
            this.credentials = latest; this.persisted = true; this.epoch += 1
            this.controllers.forEach(controller => controller.abort()); this.emitAuthChange()
            throw error
          }
          this.setAccessToken(null)
        }
        throw error
      }
    }
    const operation = navigator.locks?.request ? navigator.locks.request('lulla:auth-refresh', run) : run()
    const tracked = operation.finally(() => { if (this.refreshing === tracked) this.refreshing = undefined })
    this.refreshing = tracked
    return tracked
  }

  private emitAuthChange() {
    const session = this.authSession()
    this.authListeners.forEach(listener => listener(session))
  }

  private async request<T>(path: string, options: RequestInit & { authenticated?: boolean; responseType?: 'json' | 'blob'; timeoutMs?: number } = {}, retried = false): Promise<T> {
    const config = await loadRuntimeConfig()
    if (!config.apiBaseUrl) throw new ServerNotConfiguredError()
    const controller = new AbortController()
    let epoch = this.epoch
    this.controllers.add(controller)
    // Completed stories must finish transferring, not just return their headers.
    // Keep ordinary API calls responsive while allowing slower mobile downloads.
    const timeoutMs = options.timeoutMs ?? (options.responseType === 'blob' ? Math.max(config.requestTimeoutMs, 120_000) : config.requestTimeoutMs)
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs)
    try {
      const headers = new Headers(options.headers)
      if (options.body && !(options.body instanceof FormData)) headers.set('Content-Type', 'application/json')
      headers.set('Accept', options.responseType === 'blob' ? 'audio/*' : 'application/json')
      if (options.authenticated !== false) {
        const token = await this.ensureToken(config)
        if (token !== this.token) throw new AuthenticationRequiredError()
        headers.set('Authorization', `Bearer ${token}`); epoch = this.epoch
      }
      const fetchOptions = { ...options }
      delete fetchOptions.timeoutMs
      const response = await fetch(`${config.apiBaseUrl}${path}`, { ...fetchOptions, headers, signal: controller.signal })
      if (options.authenticated !== false && epoch !== this.epoch) throw new AuthenticationRequiredError()
      if (!response.ok) {
        const problem = await response.json().catch(() => null) as { detail?: unknown; message?: unknown; code?: string } | null
        const detail = problem?.detail as { code?: string } | undefined
        const code = detail?.code || problem?.code
        if (response.status === 401 && options.authenticated !== false && code !== 'REAUTH_REQUIRED') {
          if (!retried && this.credentials?.refreshToken) {
            // An authentication rejection happens before the protected endpoint mutation.
            await this.refreshAccessToken()
            if (epoch !== this.epoch) throw new AuthenticationRequiredError()
            return this.request<T>(path, options, true)
          }
          this.setAccessToken(null)
        }
        throw new ApiError(apiProblemMessage(problem, response.status), response.status, code)
      }
      if (response.status === 204) return undefined as T
      if (options.responseType === 'blob') {
        const blob = await response.blob()
        if (!blob.size || !/^(audio\/|application\/octet-stream(?:;|$))/i.test(blob.type)) throw new ApiError('재생할 음원 파일을 받지 못했어요. 음원을 다시 받아 주세요.', 502, 'INVALID_AUDIO_RESPONSE')
        const expected = response.headers.get('X-Content-SHA256') || response.headers.get('X-Checksum-SHA256')
        if (expected) { const hash = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', await blob.arrayBuffer())), value => value.toString(16).padStart(2, '0')).join(''); if (hash !== expected.toLowerCase()) throw new ApiError('음원 검증에 실패했어요. 다시 받아 주세요.', 502, 'CHECKSUM_MISMATCH') }
        if (options.authenticated !== false && epoch !== this.epoch) throw new AuthenticationRequiredError()
        return blob as T
      }
      const data = await response.json(); if (options.authenticated !== false && epoch !== this.epoch) throw new AuthenticationRequiredError(); return data as T
    } catch (error) {
      if (error instanceof ApiError || error instanceof ServerNotConfiguredError || error instanceof AuthenticationRequiredError) throw error
      if (error instanceof DOMException && error.name === 'AbortError') throw new ServerUnavailableError('음성 서버 응답 시간이 초과되었습니다.')
      throw new ServerUnavailableError()
    } finally { window.clearTimeout(timeout); this.controllers.delete(controller) }
  }
}

type AuthResponse = { accessToken: string; tokenType: string; expiresIn: number; user: ServerUser; refreshToken?: string; refreshExpiresIn?: number }

const extensionFor = (mime: string) => mime.includes('wav') ? 'wav' : mime.includes('mpeg') ? 'mp3' : mime.includes('ogg') ? 'ogg' : mime.includes('mp4') ? 'm4a' : 'webm'
const apiProblemMessage = (problem: { detail?: unknown; message?: unknown } | null, status: number) => {
  if (problem?.detail === 'Email is already registered') return '이미 가입된 이메일이에요. 로그인해 주세요.'
  if (problem?.detail === 'Invalid email or password') return '이메일 또는 비밀번호가 맞지 않아요.'
  if (typeof problem?.detail === 'string') return problem.detail
  if (problem?.detail && typeof problem.detail === 'object' && 'message' in problem.detail) return String(problem.detail.message)
  if (Array.isArray(problem?.detail)) {
    const messages = problem.detail.map(item => typeof item === 'object' && item && 'msg' in item ? String(item.msg) : '').filter(Boolean)
    if (messages.length) return messages.join(' ')
  }
  if (typeof problem?.message === 'string') return problem.message
  return `서버 요청에 실패했습니다. (${status})`
}
const qualityGateErrors: Record<string, string> = {
  VOICE_REFERENCE_RERECORD_REQUIRED: '새 변환 방식에 맞게 부모 목소리를 다시 녹음하고 승인해 주세요. 이전 완성본은 계속 들을 수 있어요.',
  VOICE_REFERENCE_LONG_SAMPLE_REQUIRED: '이 변환 방식은 보호자의 2분 이상 원본이 필요해요. 지금은 기기에만 준비할 수 있고 서버 변환은 아직 지원되지 않아요. 이전 완성본은 계속 들을 수 있어요.',
  VOICE_REFERENCE_PREPARATION_FAILED: '목소리 변환용 음색 정보를 준비하지 못했어요. 다시 시도해 주세요. 녹음 원본이 만료됐다면 새로 녹음해 주세요.',
  VOICE_CONVERSION_PLAN_REQUIRED: '이 동화는 발음이 끊기지 않도록 변환 구간을 확인하고 있어요. 준비가 끝나면 만들 수 있어요.',
  VOICE_CONVERSION_PLAN_INVALID: '동화의 변환 구간 자료를 다시 확인하고 있어요. 잠시 후 다시 찾아주세요.',
  VOICE_CONVERSION_PLAN_SOURCE_MISMATCH: '동화 원본과 변환 구간이 맞지 않아 제작을 멈췄어요. 확인 후 다시 이용할 수 있어요.',
  VOICE_CONVERSION_QUALITY_UNVERIFIED: '변환 사용 허가는 등록됐지만, 이 원본의 변환 품질을 검증하고 있어요.',
  VOICE_CONVERSION_QUALITY_FAILED: '변환 사용 허가는 등록됐지만, 현재 품질 검수에서 오류가 확인되어 제작을 잠시 멈췄어요.'
}
export const friendlyAudioError = (code?: string, fallback?: string) => qualityGateErrors[code ?? ''] ?? ({ SOURCE_CONVERSION_RIGHTS_REQUIRED: '이 원본의 목소리 변환 이용 권한을 확인하고 있어요.', DEVELOPMENT_SOURCE_ONLY: '개발용 원본은 구매·구독 콘텐츠로 사용할 수 없어요.', SOURCE_AUDIO_MISSING: '원본 동화 파일을 찾지 못했어요. 다른 이야기를 골라 주세요.', SOURCE_AUDIO_CORRUPT: '원본 동화 파일을 확인하고 있어요. 잠시 후 다시 시도해 주세요.', VOICE_REFERENCE_UNAVAILABLE: '등록한 부모 목소리를 불러오지 못했어요. 목소리 상태를 확인해 주세요.', VOICE_REFERENCE_CORRUPT: '부모 목소리 파일을 확인하지 못했어요. 다시 녹음하고 승인해 주세요.', VOICE_CONVERSION_FAILED: '목소리를 바꾸지 못했어요. 다시 만들기를 선택해 주세요.', VOICE_CONVERSION_DURATION_MISMATCH: '변환된 이야기 길이가 원본과 맞지 않아 저장하지 않았어요. 다시 만들기를 선택해 주세요.', VOICE_CONVERSION_NO_CHANGE: '목소리가 바뀌었는지 확인하지 못해 원본을 결과로 저장하지 않았어요. 다시 만들기를 선택해 주세요.', CONVERSION_RIGHTS_UNCONFIRMED: '이 원본의 목소리 변환 이용 권한을 확인하고 있어요.', VOICE_CONVERSION_UNAVAILABLE: '목소리 변환 서버를 준비하고 있어요. 잠시 후 다시 확인해 주세요.', SOURCE_AUDIO_UNAVAILABLE: '원본 동화 파일을 확인하고 있어요. 다른 이야기를 골라 주세요.', RECORDING_TRANSCRIPT_MISMATCH: '녹음한 문장이 안내 문장과 달라요. 문장을 처음부터 끝까지 다시 읽어주세요.', AUDIO_TEXT_MISMATCH: '동화의 일부 문장이 음원과 맞지 않아 저장하지 않았어요. 다시 만들기를 선택해 주세요.', QUALITY_UNAVAILABLE: '음원을 확인하는 기능을 잠시 사용할 수 없어요. 잠시 뒤 다시 시도해 주세요.', REAUTH_REQUIRED: '보호자 확인 시간이 지났어요. 다시 로그인한 뒤 삭제를 확인해 주세요.' }[code ?? ''] ?? fallback ?? '음성을 준비하지 못했어요. 다시 시도해 주세요.')
export function readableServerError(error: unknown) { return error instanceof DOMException && error.name === 'QuotaExceededError' ? '기기의 저장 공간이 부족해요. 저장한 음원을 정리한 뒤 다시 저장하거나 파일로 내보내 주세요.' : error instanceof ApiError ? friendlyAudioError(error.code, error.message) : error instanceof Error ? error.message : '음성 서버 요청을 처리하지 못했습니다.' }
export const voiceGenerationService: VoiceGenerationService = new HttpVoiceGenerationService()
